import { LoanConfigurationDto, NaturalPersonDto } from "api/borrower";
import { GuaranteeDto } from "api/pricing";
import { useCallback } from "react";
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext";
import { useMainBorrowerPricingSelector } from "../../contexts/main-borrower-pricing/selectors/selectors";
import { useCommonPanelForm } from "../../hooks/useCommonPanelForm";
import { AddLoanButton } from "./AddLoanButton";
import { PricingLoanCard } from "./PricingLoanCard";

interface BorrowerPricingLoanCardsProps {
  insured: NaturalPersonDto;
  loansConfigurations: LoanConfigurationDto[];
}

export const BorrowerPricingLoanCards: React.FC<BorrowerPricingLoanCardsProps> = ({
  insured,
  loansConfigurations
}) => {
  const { guaranteesReferential } = useMainBorrowerPricingState();
  const pendingLoanConfigs = useMainBorrowerPricingSelector(state => state.pendingLoanConfigs);
  const { toggleBorrowerLoan, toggleGuarantee, togglePsyBuyback } = useMainBorrowerPricingActions();
  const { onCreateLoan } = useCommonPanelForm();

  const getCurrentGuarantees = useCallback((loanId: string): string[] => {
    const currentPendingLoan = pendingLoanConfigs.find(loan => loan.loanId === loanId);
    return currentPendingLoan?.guaranteeIds || [];
  }, [pendingLoanConfigs]);

  const getIsPsyBuyback = useCallback((loanId: string): boolean => {
    const currentPendingLoan = pendingLoanConfigs.find(loan => loan.loanId === loanId);
    return currentPendingLoan?.isPsyBuyback ?? true;
  }, [pendingLoanConfigs]);

  const handleToggleGuarantee = useCallback((loanId: string, guarantee: GuaranteeDto) => {
    toggleGuarantee({ loanId, guarantee });
  }, [toggleGuarantee]);

  const handleToggleLoan = useCallback((borrowerLoanId: string) => {
    toggleBorrowerLoan(borrowerLoanId);
  }, [toggleBorrowerLoan]);

  const handleTogglePsyBuyback = useCallback((loanId: string) => {
    togglePsyBuyback({ loanId });
  }, [togglePsyBuyback]);

  if (!insured) {
    return null;
  }

  return (
    <div className="flex border rounded-xl h-28 grow overflow-hidden">
      {loansConfigurations.map((config, index) => (
        <PricingLoanCard
          key={config.loan.id}
          config={config}
          index={index}
          guaranteesReferential={guaranteesReferential}
          currentGuaranteeIds={getCurrentGuarantees(config.loan.id)}
          isPsyBuyback={getIsPsyBuyback(config.loan.id)}
          onToggleLoan={handleToggleLoan}
          onToggleGuarantee={handleToggleGuarantee}
          onTogglePsyBuyback={handleTogglePsyBuyback}
        />
      ))}
      {loansConfigurations.length === 1 && (
        <AddLoanButton onClick={onCreateLoan} />
      )}
    </div>
  );
};
